import { Injectable, InjectionToken, inject, signal } from '@angular/core';
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut } from 'aws-amplify/auth';

/**
 * Funções do Amplify expostas como tokens injetáveis — desacopla o serviço do
 * módulo `aws-amplify/auth`, tornando-o testável via DI (sem mock de módulo,
 * que é frágil no Vitest quando outro spec importa o módulo sem mock).
 */
export const GET_CURRENT_USER = new InjectionToken<typeof getCurrentUser>('GET_CURRENT_USER', {
  providedIn: 'root',
  factory: () => getCurrentUser,
});
export const FETCH_AUTH_SESSION = new InjectionToken<typeof fetchAuthSession>('FETCH_AUTH_SESSION', {
  providedIn: 'root',
  factory: () => fetchAuthSession,
});
export const SIGN_IN_WITH_REDIRECT = new InjectionToken<typeof signInWithRedirect>('SIGN_IN_WITH_REDIRECT', {
  providedIn: 'root',
  factory: () => signInWithRedirect,
});
export const SIGN_OUT = new InjectionToken<typeof signOut>('SIGN_OUT', {
  providedIn: 'root',
  factory: () => signOut,
});

export interface AuthUser {
  readonly sub: string;
  readonly name: string;
  readonly email: string;
  readonly initials: string;
}

/**
 * Autenticação via Cognito (Amplify, OAuth/Hosted UI). Mantém o usuário atual
 * num signal (para o shell) e expõe o access token para o interceptor.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _getCurrentUser = inject(GET_CURRENT_USER);
  private readonly _fetchAuthSession = inject(FETCH_AUTH_SESSION);
  private readonly _signInWithRedirect = inject(SIGN_IN_WITH_REDIRECT);
  private readonly _signOut = inject(SIGN_OUT);

  readonly user = signal<AuthUser | null>(null);

  async isAuthenticated(): Promise<boolean> {
    try {
      await this._getCurrentUser();
      return true;
    } catch {
      return false;
    }
  }

  /** Access token atual (usado pelo interceptor / authorizer GetUser). */
  async accessToken(): Promise<string | null> {
    try {
      const { tokens } = await this._fetchAuthSession();
      return tokens?.accessToken ? tokens.accessToken.toString() : null;
    } catch {
      return null;
    }
  }

  /** Recarrega o usuário a partir das claims do ID token e atualiza o signal. */
  async refreshUser(): Promise<AuthUser | null> {
    try {
      await this._getCurrentUser();
      const { tokens } = await this._fetchAuthSession();
      const claims = (tokens?.idToken?.payload ?? {}) as Record<string, unknown>;
      const user = this.buildUser(claims);
      this.user.set(user);
      return user;
    } catch {
      this.user.set(null);
      return null;
    }
  }

  /** Redireciona para o Hosted UI do Cognito. */
  async login(): Promise<void> {
    await this._signInWithRedirect({ options: { lang: 'pt-BR' } });
  }

  /** Encerra a sessão (limpa local + redireciona ao logout do Cognito). */
  async logout(): Promise<void> {
    this.user.set(null);
    await this._signOut();
  }

  private buildUser(claims: Record<string, unknown>): AuthUser {
    const sub = typeof claims['sub'] === 'string' ? (claims['sub'] as string) : '';
    const email = typeof claims['email'] === 'string' ? (claims['email'] as string) : '';
    const rawName = claims['name'];
    const name = typeof rawName === 'string' && rawName ? rawName : email;
    return {
      sub,
      name,
      email,
      initials: this.initials(name || email),
    };
  }

  private initials(value: string): string {
    const parts = value.trim().split(/\s+/).filter(Boolean);
    if (!parts.length) {
      return '?';
    }
    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
}
